import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, TrendingUp, Globe, ShoppingCart, Truck } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const Prologis2026Article = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-8">
        <div className="flex justify-between items-center mb-6">
          <Button 
            variant="ghost" 
            onClick={() => window.history.back()}
            className="hover:bg-gray-100"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common.backToHighlights')}
          </Button>
          <LanguageSwitcher />
        </div>

        <article className="max-w-4xl mx-auto">
          <header className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <Badge className="bg-purple-500 text-white">
                <TrendingUp className="h-3 w-3 mr-1" />
                {t('article.prologis2026.badge')}
              </Badge>
              <span className="text-gray-500">{t('article.prologis2026.date')}</span>
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              {t('article.prologis2026.title')}
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              {t('article.prologis2026.summary')}
            </p>
          </header>

          <div className="mb-8">
            <img 
              src="https://market-insights.upply.com/hs-fs/hubfs/webinar-ti-iru-upply/2025/Q3%202025/Cover%20rapport.jpg?width=730&height=395&name=Cover%20rapport.jpg" 
              alt="European road freight market recovery and rate analysis" 
              className="w-full h-64 object-cover rounded-lg shadow-lg"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Globe className="h-5 w-5 mr-2 text-blue-600" />
                  {t('prologis2026.stats.global.label')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-blue-600">{t('prologis2026.stats.global.value')}</div>
                <p className="text-sm text-gray-600">{t('prologis2026.stats.global.desc')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <ShoppingCart className="h-5 w-5 mr-2 text-green-600" />
                  {t('prologis2026.stats.ecommerce.label')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-green-600">{t('prologis2026.stats.ecommerce.value')}</div>
                <p className="text-sm text-gray-600">{t('prologis2026.stats.ecommerce.desc')}</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Truck className="h-5 w-5 mr-2 text-orange-600" />
                  {t('prologis2026.stats.transport.label')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-orange-600">{t('prologis2026.stats.transport.value')}</div>
                <p className="text-sm text-gray-600">{t('prologis2026.stats.transport.desc')}</p>
              </CardContent>
            </Card>
          </div>

          <div className="prose max-w-none">
            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('prologis2026.section1.title')}</h3>
            <div className="bg-blue-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">{t('prologis2026.section1.subtitle')}</h4>
              <div className="whitespace-pre-line text-sm text-gray-700">
                {t('prologis2026.section1.content')}
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('prologis2026.section2.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('prologis2026.section2.content')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('prologis2026.section3.title')}</h3>
            <div className="bg-green-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">{t('prologis2026.section3.subtitle')}</h4>
              <div className="whitespace-pre-line text-sm text-gray-700">
                {t('prologis2026.section3.content')}
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('prologis2026.section4.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('prologis2026.section4.content')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('prologis2026.section5.title')}</h3>
            <div className="bg-purple-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">{t('prologis2026.section5.subtitle')}</h4>
              <div className="whitespace-pre-line text-sm text-gray-700">
                {t('prologis2026.section5.content')}
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('prologis2026.section6.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('prologis2026.section6.content')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('prologis2026.section7.title')}</h3>
            <div className="bg-orange-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">{t('prologis2026.section7.subtitle')}</h4>
              <div className="whitespace-pre-line text-sm text-gray-700">
                {t('prologis2026.section7.content')}
              </div>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('prologis2026.section8.title')}</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              {t('prologis2026.section8.content')}
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">{t('prologis2026.section9.title')}</h3>
            <div className="whitespace-pre-line text-gray-700 mb-6 pl-6">
              {t('prologis2026.section9.content')}
            </div>

            <div className="bg-gray-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">Source Information:</h4>
              <p className="text-sm text-gray-600 mb-2">
                {t('prologis2026.source')}
              </p>
              <Button 
                variant="outline" 
                size="sm" 
                className="text-blue-600 border-blue-200"
                onClick={() => window.open('https://market-insights.upply.com/en/european-road-freight-spot-and-contract-rates-rise-in-q3', '_blank')}
              >
                View Full Research Report
              </Button>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default Prologis2026Article;